import { getCrdtAlgorithmFromConfigOrDefault } from '../crdts/CrdtAlgorithmRegistry.js';
import { getRecipe, isVersionedObject, resolveRuleInheritance } from '../object-recipes.js';
import { storeUnversionedObject } from '../storage-unversioned-objects.js';
import { storeVersionedObject, storeVersionedObjectNoMerge } from '../storage-versioned-objects.js';
export async function iterateObjectsMerge(type, tree, cb) {
    const recipe = getRecipe(type);
    const newObj = {
        ...(await iterateObjectMerge({ type: 'object', rules: recipe.rule }, '', cb, {
            crdtConfig: recipe.crdtConfig || new Map(),
            tree
        })),
        $type$: type
    };
    // Skip dispatch - caller (mergeVersionWithCurrent) handles dispatch after merge
    return storeVersionedObjectNoMerge(newObj, true);
}
async function iterateRecipeValueMerge(recipeValue, path, cb, params) {
    const crdtAlgorithm = getCrdtAlgorithmFromConfigOrDefault(params.crdtConfig, path, recipeValue.type);
    const cbArgs = {
        path,
        valueType: recipeValue,
        tree: await params.tree.createTreeForSubPath(`${path}#${crdtAlgorithm.id}`),
        crdtAlgorithm
    };
    switch (recipeValue.type) {
        case 'string':
            return cb.string(cbArgs);
        case 'integer':
            return cb.integer(cbArgs);
        case 'number':
            return cb.number(cbArgs);
        case 'boolean':
            return cb.boolean(cbArgs);
        case 'referenceToObj':
            return iterateReferenceToObjectMerge(recipeValue, path, cb, params);
        case 'referenceToId':
            return cb.referenceToId(cbArgs);
        case 'referenceToClob':
            return cb.referenceToClob(cbArgs);
        case 'referenceToBlob':
            return cb.referenceToBlob(cbArgs);
        case 'map':
            if (crdtAlgorithm.algoType === 'NotAvailable') {
                return iterateMapMerge(recipeValue, path, cb, params);
            }
            else {
                return cb.map(cbArgs);
            }
        case 'bag':
            return cb.bag(cbArgs);
        case 'array':
            return cb.array(cbArgs);
        case 'set':
            return cb.set(cbArgs);
        case 'object':
            if (crdtAlgorithm.algoType === 'NotAvailable') {
                return iterateObjectMerge(recipeValue, path, cb, params);
            }
            else {
                return cb.object(cbArgs);
            }
        case 'stringifiable':
            return cb.stringifiable(cbArgs);
        default:
            throw new Error('Found unexpected ValueType!');
    }
}
async function iterateReferenceToObjectMerge(recipeValue, path, cb, params) {
    const crdtAlgorithm = getCrdtAlgorithmFromConfigOrDefault(params.crdtConfig, path, 'referenceToObj');
    const result = await cb.referenceToObj({
        path,
        tree: await params.tree.createTreeForSubPath(`${path}#${crdtAlgorithm.id}`, false),
        valueType: recipeValue,
        crdtAlgorithm
    });
    if (result.action === 'set') {
        return result.value;
    }
    else {
        const newObj = {
            ...(await iterateObjectMerge({ type: 'object', rules: getRecipe(result.type).rule }, path, cb, {
                crdtConfig: params.crdtConfig,
                tree: result.tree
            })),
            $type$: result.type
        };
        if (isVersionedObject(newObj)) {
            // TODO: This should not write to the version map
            return (await storeVersionedObject(newObj)).hash;
        }
        else {
            return (await storeUnversionedObject(newObj)).hash;
        }
    }
}
async function iterateObjectMerge(recipeValue, path, cb, params) {
    const crdtAlgorithm = getCrdtAlgorithmFromConfigOrDefault(params.crdtConfig, path, 'object');
    const childTree = await params.tree.createTreeForSubPath(`${path}#${crdtAlgorithm.id}`);
    if (crdtAlgorithm.algoType !== 'NotAvailable') {
        return cb.object({
            path: path,
            tree: childTree,
            valueType: recipeValue,
            crdtAlgorithm
        });
    }
    const resultObj = {
        ...(childTree.commonHistoryNode.type === 'empty'
            ? childTree.firstMergeNode.data
            : childTree.commonHistoryNode.data)
    };
    const changedProps = new Set(params.tree.getChangedSubPaths(path, true).map(p => p.split('.')[0]));
    for (const rawRule of recipeValue.rules) {
        const rule = resolveRuleInheritance(rawRule);
        const itemtype = rule.itemtype || { type: 'string' };
        // Skip children that have no changes
        if (!changedProps.has(rule.itemprop)) {
            continue;
        }
        const subPath = path === '' ? rule.itemprop : path.concat('.', rule.itemprop);
        const crdtAlgorithmProp = getCrdtAlgorithmFromConfigOrDefault(params.crdtConfig, subPath, 'objectProperty');
        const result = rule.optional
            ? await cb.objectProperty({
                path: subPath,
                tree: await params.tree.createTreeForSubPath(`${subPath}#${crdtAlgorithmProp.id}`, false),
                valueType: itemtype,
                crdtAlgorithm: crdtAlgorithmProp
            })
            : {
                action: 'iterate',
                tree: params.tree
            };
        if (result.action === 'set') {
            resultObj[rule.itemprop] = result.value;
        }
        else if (result.action === 'delete') {
            delete resultObj[rule.itemprop];
        }
        else {
            resultObj[rule.itemprop] = await iterateRecipeValueMerge(itemtype, subPath, cb, {
                crdtConfig: params.crdtConfig,
                tree: result.tree
            });
        }
    }
    return resultObj;
}
async function iterateMapMerge(recipeValue, path, cb, params) {
    const crdtAlgorithm = getCrdtAlgorithmFromConfigOrDefault(params.crdtConfig, path, 'map');
    const childTree = await params.tree.createTreeForSubPath(`${path}#${crdtAlgorithm.id}`);
    if (crdtAlgorithm.algoType !== 'NotAvailable') {
        return cb.map({
            path: path,
            tree: childTree,
            valueType: recipeValue,
            crdtAlgorithm
        });
    }
    const resultMap = childTree.commonHistoryNode.type === 'empty'
        ? new Map(childTree.firstMergeNode.data)
        : new Map(childTree.commonHistoryNode.data);
    const changedKeys = new Set(params.tree.getChangedSubPaths(path, true).map(p => p.split('.')[0]));
    for (const key of changedKeys) {
        const subPath = path === '' ? key : path.concat('.', key);
        const crdtAlgorithmElem = getCrdtAlgorithmFromConfigOrDefault(params.crdtConfig, subPath, 'mapEntry');
        const result = await cb.mapEntry({
            path: subPath,
            tree: await params.tree.createTreeForSubPath(`${subPath}#${crdtAlgorithmElem.id}`),
            valueType: recipeValue,
            crdtAlgorithm: crdtAlgorithmElem
        });
        if (result.action === 'set') {
            resultMap.set(key, result.value);
        }
        else if (result.action === 'delete') {
            resultMap.delete(key);
        }
        else {
            resultMap.set(key, await iterateRecipeValueMerge(recipeValue.value, subPath, cb, {
                crdtConfig: params.crdtConfig,
                tree: result.tree
            }));
        }
    }
    return resultMap;
}
//# sourceMappingURL=iterate-objects-merge.js.map